/**
 * The repo's i18n PRIMITIVES: which way a language reads, and how one catalogue entry becomes
 * one string. Pure — no store, no React, no platform API, no dependency.
 *
 * 🔴 **This module is deliberately module-agnostic.** The kiosk is the first consumer, not the
 * only one: POS web and POS mobile follow. So nothing here knows about a kiosk config, a
 * `kioskStore`, or a screen. Each host resolves its OWN language (the kiosk does it in
 * `@rista/core/kiosk/language`) and owns its OWN catalogue; what they share is this file.
 *
 * It sits beside `utils/currency` on purpose and is the same kind of thing: a display concern,
 * pure formatters, a merchant seam, consumed through a `packages/ui` context. Constants and
 * logic in one module — never a constants file plus a helper body somewhere else.
 */

/* ── direction ─────────────────────────────────────────────────────────────────────────────── */

export type Direction = "ltr" | "rtl";

export const DEFAULT_LANGUAGE = "en";
export const DEFAULT_DIRECTION: Direction = "ltr";

/**
 * Primary subtags that read right-to-left, as BCP-47 spells them.
 *
 * ⚠️ **Not `new Intl.Locale(tag).maximize().script`**, which is how react-native-web does it
 * internally. Hermes' `Intl` coverage differs across Android and iOS and `Intl.Locale` is one of
 * the pieces that can be missing, so the one function every screen's direction depends on would
 * answer `ltr` for Arabic on some devices and `rtl` on others. A `Set.has` on a string cannot.
 *
 * Both spellings of Hebrew (`he`, and the deprecated `iw`) and of Persian (`fa`, `far`) are here
 * because a merchant config is hand-entered and either can arrive.
 */
const RTL_LANGUAGES = new Set([
  "ae", // Avestan
  "ar", // Arabic
  "arc", // Aramaic
  "ckb", // Sorani Kurdish
  "dv", // Dhivehi
  "fa",
  "far", // Persian
  "he",
  "iw", // Hebrew
  "ks", // Kashmiri
  "ku", // Kurdish
  "nqo", // N'Ko
  "pnb", // Western Punjabi
  "ps", // Pashto
  "sd", // Sindhi
  "ug", // Uyghur
  "ur", // Urdu
  "yi", // Yiddish
]);

/**
 * `"ar-AE"` -> true, `"en-IN"` -> false. Reads the PRIMARY subtag only, so a region or a script
 * suffix cannot change the answer, and an empty or malformed tag is `ltr` rather than a throw —
 * this runs in front of a customer and an unrecognised config value must not blank the screen.
 */
export const isRtlLanguage = (tag?: string | null): boolean =>
  RTL_LANGUAGES.has(String(tag ?? "").toLowerCase().split("-")[0]);

export const directionFor = (tag?: string | null): Direction =>
  isRtlLanguage(tag) ? "rtl" : "ltr";

/**
 * A language's name IN ITSELF — `"ar-AE"` -> `"العربية"`, `"en"` -> `"English"`.
 *
 * What a language picker must show: someone who reads only Arabic cannot find "Arabic" in a
 * list written in English. An endonym is the same in every locale, so this is data, not copy —
 * it belongs here and never in a catalogue.
 *
 * ⚠️ **Not `Intl.DisplayNames`.** Hermes' `Intl` coverage is uneven across Android and iOS and
 * this is one of the pieces that can be missing, so the picker would render tags on some
 * devices; it also answers in the *asking* locale by default, which is the opposite of what a
 * picker wants. A map of the languages we actually support is smaller, total, and correct.
 *
 * Falls back to the tag itself, so an unmapped language is still selectable — visibly ugly,
 * which is the right kind of failure for a list nobody translated yet.
 */
const LANGUAGE_ENDONYMS: Record<string, string> = {
  ar: "العربية",
  de: "Deutsch",
  en: "English",
  es: "Español",
  fa: "فارسی",
  fr: "Français",
  he: "עברית",
  hi: "हिन्दी",
  id: "Bahasa Indonesia",
  kn: "ಕನ್ನಡ",
  ml: "മലയാളം",
  mr: "मराठी",
  ta: "தமிழ்",
  te: "తెలుగు",
  th: "ไทย",
  ur: "اردو",
  vi: "Tiếng Việt",
  zh: "中文",
};

export const languageLabel = (tag: string): string =>
  LANGUAGE_ENDONYMS[String(tag ?? "").toLowerCase().split("-")[0]] ?? tag;

/* ── catalogues ────────────────────────────────────────────────────────────────────────────── */

/** CLDR's plural categories. Written out rather than taken from `Intl.LDMLPluralRule`, for the
 *  same reason as the constructor below — that type lives in `lib.es2018.intl`, which not every
 *  program compiling this file loads. */
export type PluralCategory = "zero" | "one" | "two" | "few" | "many" | "other";

/**
 * One catalogue entry. A plain string, or plural forms.
 *
 * `other` is REQUIRED on the plural shape, and that is load-bearing: CLDR guarantees every
 * locale has an `other`, so a plural entry can always resolve to something even when the count
 * lands in a category this language's author did not write. English needs `one` + `other`;
 * Arabic has all six.
 */
export type CopyValue = string | ({ other: string } & Partial<Record<PluralCategory, string>>);

export type Catalogue<K extends string> = Record<K, CopyValue>;

/** Values substituted into `${name}` placeholders. `count` additionally picks the plural form. */
export interface CopyParams {
  [name: string]: string | number | undefined;
}

/**
 * `Intl.PluralRules` as an OPTIONAL global.
 *
 * Two independent reasons it is typed here rather than used off `Intl` directly:
 *   · it is declared in `lib.es2018.intl.d.ts`, and `apps/web`'s program is `target: es6` with
 *     no `lib` — so `Intl.PluralRules` is a compile error there, in a file `apps/web` compiles;
 *   · a stripped Hermes build genuinely may not ship it, so "might be undefined" is the truth
 *     about this API and the type should say so.
 */
const pluralRules = (
  Intl as unknown as {
    PluralRules?: new (locale?: string) => { select(n: number): string };
  }
).PluralRules;

const pluralCategory = (count: number, locale: string): PluralCategory => {
  if (!pluralRules) return count === 1 ? "one" : "other";
  try {
    return new pluralRules(locale).select(count) as PluralCategory;
  } catch {
    // RangeError on a malformed tag. English's rule is the honest fallback; it is also the only
    // one that is right for the language every catalogue is written in first.
    return count === 1 ? "one" : "other";
  }
};

/** `${name}`, the placeholder syntax `apps/mobile/src/constants/strings.ts` already writes — so
 *  that file becomes a catalogue by annotating its type, without editing 191 values. */
const PLACEHOLDER = /\$\{(\w+)\}/g;

const interpolate = (template: string, params: CopyParams): string =>
  template.replace(PLACEHOLDER, (whole, name: string) => {
    const value = params[name];
    // An unsupplied placeholder is left VISIBLE rather than blanked. `Select up to ${max}` on
    // screen is a bug report; `Select up to ` is a bug nobody files.
    return value === undefined ? whole : String(value);
  });

/**
 * One catalogue entry -> one string. The whole engine.
 *
 * ```ts
 * resolveCopy(en, "cart.itemsAdded", { count: 2 })      // "2 items added"
 * resolveCopy(en, "search.noMatches", { term: "fries" }) // 'No matches for "fries"'
 * ```
 *
 * `params.count` is what selects the plural form — the convention every i18n library uses, so
 * nobody has to look it up.
 *
 * **A missing key returns the key.** The catalogue types make that near-unreachable (a
 * `Catalogue<K>` is a total `Record`), but a kiosk in front of a customer must degrade to an
 * ugly string rather than throw and white-screen the terminal. It is also the seam a
 * base-language fallback hangs off later: a provider holding two catalogues writes
 * `active[key] ?? base[key]` itself, which is why that fallback is not built in here.
 */
export function resolveCopy<K extends string>(
  catalogue: Catalogue<K>,
  key: K,
  params?: CopyParams,
  locale: string = DEFAULT_LANGUAGE,
): string {
  const entry = catalogue[key];
  if (entry == null) return key;

  let template: string;
  if (typeof entry === "string") {
    template = entry;
  } else {
    const count = params?.count;
    template =
      typeof count === "number"
        ? (entry[pluralCategory(count, locale)] ?? entry.other)
        : entry.other;
  }

  return params ? interpolate(template, params) : template;
}
